import { mapState, mapMutations } from 'vuex'
import { SET_LOAD } from 'vx/mutations'
import { LoadDecorator } from 'decorators'
import { padStart, sumBy, reduce } from 'lodash'
import accounting from 'accounting'

export default {
  computed: {
    ...mapState('job', ['job']),

    /**
     * Get the client cost of the load including additional charges.
     *
     * @return number
     */
    clientCharge () {
      if (!this.haveWeighbridge || this.load.weighbridge.status !== 'approved') {
        return 0
      }

      return (this.job.rate * this.load.weighbridge.total_tonnage) + this.additionalChargesTotal
    },

    /**
     * Get sum of loan charges
     *
     * @returns {number}
     */
    additionalChargesTotal () {
      return sumBy(this.load.charges, charge => {
        const rate = this.$user.isCarrierOrDriver ? charge.carrier_rate : charge.client_rate

        if (charge.type === 'single') {
          return rate
        }

        return rate * (this.load.weighbridge && this.load.weighbridge.total_tonnage) || 0
      })
    },

    /**
     * Get additionalChargesTotal as money.
     *
     * @returns {string|[]}
     */
    additionalChargesTotalAsMoney () {
      if (this.additionalChargesTotal === 0) {
        return 'N/A'
      }

      return accounting.formatMoney(this.additionalChargesTotal)
    },

    /**
     * Get the client cost as money.
     *
     * @returns {string|[]}
     */
    clientChargeAsMoney () {
      if (this.clientCharge === 0) {
        return 'N/A'
      }

      return accounting.formatMoney(this.clientCharge)
    },

    carrierProfit () {
      if (!this.haveWeighbridge || this.load.weighbridge.status !== 'approved') {
        return 0
      }

      return (this.load.rate * this.load.weighbridge.total_tonnage) + this.additionalChargesTotal
    },

    carrierProfitAsMoney () {
      if (this.carrierProfit === 0) {
        return 'N/A'
      }

      return accounting.formatMoney(this.carrierProfit)
    },

    /**
     * Calculate profit of load based on (jobRate - loadRate) * loadSize
     *
     * @return number|float
     */
    arrowProfit () {
      let loadSize

      if (this.haveWeighbridge && this.load.weighbridge.status === 'approved') {
        loadSize = this.load.weighbridge.total_tonnage
      } else {
        loadSize = parseFloat(this.load.size) || undefined
      }

      if (loadSize === undefined) {
        return this.rateDifference
      }

      return this.rateDifference * loadSize
    },

    /**
     * Get the difference in job rate and load rate.
     *
     * @return number
     */
    rateDifference () {
      return (parseFloat(this.job.rate) || 0) - (parseFloat(this.load.rate) || 0)
    },

    /**
     * Get the rate difference as money.
     *
     * @return string
     */
    perTonneDifference () {
      return `${accounting.formatMoney(this.rateDifference)} per tonne`
    },

    /**
     * Get the profit as money.
     *
     * @return string
     */
    arrowProfitAsMoney () {
      return accounting.formatMoney(this.arrowProfit)
    },

    /**
     * Determine if load is profitable.
     *
     * @return boolean
     */
    isProfitableLoadForArrow () {
      return this.arrowProfit > 0
    },

    /**
     * Determine if load is not profitable.
     *
     * @return boolean
     */
    isNotProfitableLoadForArrow () {
      return this.arrowProfit <= 0 || this.arrowProfit === undefined
    },

    /**
     * Get the load number based on it's index plus 1 padded with 0s.
     *
     * @return string
     */
    number () {
      return padStart(this.index + 1, 2, 0)
    },

    /**
     * Decorate the load.
     *
     * @return object
     */
    decorated () {
      return new LoadDecorator(this.load)
    },

    /**
     * Determine if we have charges.
     *
     * @return boolean
     */
    haveCharges () {
      return this.load.charges !== null &&
        this.load.charges !== undefined &&
        this.load.charges.length > 0
    },

    /**
     * Determine if we have a weighbridge. Only returns true if weighbridge has dockets.
     *
     * @return boolean
     */
    haveWeighbridge () {
      return this.load.weighbridge !== null &&
        this.load.weighbridge !== undefined &&
        this.load.weighbridge.dockets.length > 0
    },

    dockets () {
      if (this.load.weighbridge) {
        return reduce(this.load.weighbridge.dockets, (dockets, docket, index) => {
          if (docket.number) {
            if (index > 0) dockets += '<br/>'
            dockets += docket.number
          }
          return dockets
        }, '')
      }
      return ''
    }
  },

  methods: {
    ...mapMutations('load', [SET_LOAD]),

    /**
     * Set the active load.
     *
     * @return void
     */
    setActiveLoad () {
      this[SET_LOAD](this.load)
    }
  }
}



// WEBPACK FOOTER //
// ./src/mixins/loads/load-helpers.js